"use client";

import { useMemo, useRef, useState } from "react";
import { formOptions } from "@tanstack/react-form";
import { useAppForm } from "../shared/hooks/form";
import { useTranslations } from "next-intl";
import {
  PROFESSOR_IMPACT_FORM_DEFAULTS,
  fieldConfig,
  referralSourceOptions,
  REFERRAL_SOURCE_OTHER_VALUE,
} from "./constants";
import { requiredString, emailValidator, urlValidator, termsAgreedValidator } from "../shared/validators";
import { createValidationMessages, enrichWithUTMs } from "../shared/utils";
import type { ProfessorImpactFormValues, ProfessorImpactPayload } from "./types";
import type { HubSpotSubmitResult } from "@/app/_api/hubspot/shared/types";
import { SubmitButton } from "../shared/SubmitButton";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";

const formOpts = formOptions({
  defaultValues: PROFESSOR_IMPACT_FORM_DEFAULTS,
});

export interface ProfessorImpactFormProps {
  onSubmit: (payload: ProfessorImpactPayload) => Promise<HubSpotSubmitResult>;
  /** Called after a successful submission. */
  onSuccess?: () => void;
}

export function ProfessorImpactForm({ onSubmit, onSuccess }: ProfessorImpactFormProps) {
  const t = useTranslations("ClientKeys.professor_impact_form");
  const tValidation = useTranslations("ClientKeys.forms.validation");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { captureEvent } = useAnalytics();

  const messages = useMemo(() => createValidationMessages(tValidation), [tValidation]);
  const validators = useMemo(
    () => ({
      firstName: requiredString(messages, 100),
      lastName: requiredString(messages, 100),
      email: emailValidator(messages),
      institution: requiredString(messages, 500),
      website: urlValidator(messages),
      useCase: requiredString(messages, 5000),
      studentAccess: requiredString(messages, 2000),
      referralSource: requiredString(messages, 500),
      referralSourceOther: ({
        value,
        fieldApi,
      }: {
        value: string;
        fieldApi: {
          form: {
            getFieldValue: <K extends keyof ProfessorImpactFormValues>(field: K) => ProfessorImpactFormValues[K];
          };
        };
      }) => {
        const referralSource = fieldApi.form.getFieldValue("referralSource");
        if (referralSource !== REFERRAL_SOURCE_OTHER_VALUE) return undefined;
        if (!value || value.trim().length === 0) return messages.required;
        if (value.length > 2000) return messages.maxLength(2000);
        return undefined;
      },
      termsAgreed: termsAgreedValidator(messages),
    }),
    [messages],
  );

  const referralSourceFieldOptions = useMemo(
    () => referralSourceOptions.map(opt => ({ label: t(opt.translationKey), value: opt.value })),
    [t],
  );

  const form = useAppForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      setSubmitError(null);

      const payload: ProfessorImpactPayload = enrichWithUTMs(value);

      try {
        const result = await onSubmit(payload);
        if (result.success) {
          captureEvent(EventName.mw_professor_impact_form_submitted, {});
          onSuccess?.();
        } else {
          setSubmitError(result.error || t("errors.submission_failed"));
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to submit professor impact form");
        setSubmitError(t("errors.submission_failed"));
        throw error;
      }
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const legalLinkClassName = "tw-underline tw-underline-offset-2 hover:tw-no-underline";
  const privacyPolicyUrl = "https://elevenlabs.io/privacy-policy";
  const professorTermsUrl = "https://elevenlabs.io/elevenlabs-impact-program-for-professors-terms-conditions";

  return (
    <div ref={containerRef} className='tw-w-full'>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className='tw-flex tw-flex-col md:tw-flex-row md:tw-gap-4'>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fieldConfig.firstName.name}
              validators={{ onMount: validators.firstName, onChange: validators.firstName }}
            >
              {field => (
                <field.Input
                  name={fieldConfig.firstName.name}
                  label={t("fields.first_name.label")}
                  placeholder={t("fields.first_name.placeholder")}
                  required={fieldConfig.firstName.required}
                />
              )}
            </form.AppField>
          </div>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fieldConfig.lastName.name}
              validators={{ onMount: validators.lastName, onChange: validators.lastName }}
            >
              {field => (
                <field.Input
                  name={fieldConfig.lastName.name}
                  label={t("fields.last_name.label")}
                  placeholder={t("fields.last_name.placeholder")}
                  required={fieldConfig.lastName.required}
                />
              )}
            </form.AppField>
          </div>
        </div>
        <form.AppField
          name={fieldConfig.email.name}
          validators={{ onMount: validators.email, onChange: validators.email }}
        >
          {field => (
            <field.Input
              name={fieldConfig.email.name}
              label={t("fields.email.label")}
              placeholder={t("fields.email.placeholder")}
              type='email'
              required={fieldConfig.email.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.institution.name}
          validators={{ onMount: validators.institution, onChange: validators.institution }}
        >
          {field => (
            <field.Input
              name={fieldConfig.institution.name}
              label={t("fields.institution.label")}
              placeholder={t("fields.institution.placeholder")}
              required={fieldConfig.institution.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.website.name}
          validators={{ onMount: validators.website, onChange: validators.website }}
        >
          {field => (
            <field.Input
              name={fieldConfig.website.name}
              label={t("fields.website.label")}
              placeholder={t("fields.website.placeholder")}
              required={fieldConfig.website.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.useCase.name}
          validators={{ onMount: validators.useCase, onChange: validators.useCase }}
        >
          {field => (
            <field.Textarea
              name={fieldConfig.useCase.name}
              label={t("fields.use_case.label")}
              placeholder={t("fields.use_case.placeholder")}
              required={fieldConfig.useCase.required}
              rows={5}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.studentAccess.name}
          validators={{ onMount: validators.studentAccess, onChange: validators.studentAccess }}
        >
          {field => (
            <field.Textarea
              name={fieldConfig.studentAccess.name}
              label={t("fields.student_access.label")}
              placeholder={t("fields.student_access.placeholder")}
              required={fieldConfig.studentAccess.required}
              rows={5}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.referralSource.name}
          validators={{ onMount: validators.referralSource, onChange: validators.referralSource }}
        >
          {field => (
            <field.Select
              name={fieldConfig.referralSource.name}
              label={t("fields.referral_source.label")}
              unselectedLabel={t("fields.referral_source.unselectedLabel")}
              options={referralSourceFieldOptions}
              required={fieldConfig.referralSource.required}
              portalContainer={containerRef}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={state => state.values.referralSource}>
          {referralSource =>
            referralSource === REFERRAL_SOURCE_OTHER_VALUE && (
              <form.AppField
                name={fieldConfig.referralSourceOther.name}
                validators={{
                  onChangeListenTo: ["referralSource"],
                  onMount: validators.referralSourceOther,
                  onChange: validators.referralSourceOther,
                }}
              >
                {field => (
                  <field.Textarea
                    name={fieldConfig.referralSourceOther.name}
                    label={t("fields.referral_source_other.label")}
                    placeholder={t("fields.referral_source_other.placeholder")}
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
            )
          }
        </form.Subscribe>

        <form.AppField name={fieldConfig.featuredConsent.name}>
          {field => (
            <field.FormCheckbox
              name={fieldConfig.featuredConsent.name}
              label={t("fields.featured_consent.label")}
              required={fieldConfig.featuredConsent.required}
            />
          )}
        </form.AppField>

        <form.AppField
          name={fieldConfig.termsAgreed.name}
          validators={{ onMount: validators.termsAgreed, onChange: validators.termsAgreed }}
        >
          {field => (
            <field.FormCheckbox
              name={fieldConfig.termsAgreed.name}
              label={t.rich("fields.terms.label", {
                termsLink: chunks => (
                  <a href={professorTermsUrl} target='_blank' rel='noopener noreferrer' className={legalLinkClassName}>
                    {chunks}
                  </a>
                ),
                privacyPolicy: chunks => (
                  <a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer' className={legalLinkClassName}>
                    {chunks}
                  </a>
                ),
              })}
              required={fieldConfig.termsAgreed.required}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
          {({ canSubmit, isSubmitting }) => (
            <SubmitButton
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              submitLabel={t("buttons.submit")}
              submittingLabel={t("buttons.submitting")}
              submitError={submitError}
            />
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
